import React, {  useState, Fragment, useEffect } from 'react';
import styled from 'styled-components/macro';
import { FieldArray, FormikHandlers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { MarketingDropdown } from '../../common/components';
import { Table, Tr, Tbody, Td } from '../../../components/Table';
import { Item } from '../../types';
import { DropdownOption } from '../../common/components/MarketingDropdown';
import { State } from '../reducer';
import { border, colors, distances } from '../../../styles/constants';
import { Checkbox, Input } from '../../../components/Forms';
import Icon from '../../../components/Icons';
import { Button } from '../../../components/Buttons';
import { LoadingOverlay } from '../../../components/Loading';

interface ItemsSelectionProps {
    itemsSelectionState: State;
    fetchProductsCatalogs: (accountId: string) => void;
    fetchProducts: (accountId: string, catalogId: string) => void;
    name: string;
    items: Item[];
    handleBlur: FormikHandlers['handleBlur'];
}

interface SelectedProduct {
    name: string;
    id: string;
    groupId: string;
}

const ItemsSelection = (
    {
        itemsSelectionState,
        fetchProductsCatalogs,
        fetchProducts,
        name,
        items,
        handleBlur,
    }: ItemsSelectionProps
) => {
    const { t } = useTranslation();
    const { accountId } = useParams();

    const [selectedCatalog, setSelectedCatalog] = useState<DropdownOption | undefined>(undefined);
    const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
    const [manuallyAddedIds, setManuallyAddedIds] = useState<string>('');
    const [isVisible, setIsVisible] = useState<boolean>(false);

    useEffect(() => {
        if (accountId) {
            fetchProductsCatalogs(accountId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (accountId && selectedCatalog) {
            fetchProducts(accountId, selectedCatalog.value);
        }
    }, [accountId, fetchProducts, selectedCatalog]);

    const catalogs = itemsSelectionState.productsCatalogs;
    if (!catalogs) {
        return <LoadingOverlay />;
    }

    const catalogsAsOptions = catalogs.map((catalog) => ({
        label: catalog.catalog_name,
        value: catalog.catalog_id,
    }));

    if (!selectedCatalog && catalogs.length > 0) {
        setSelectedCatalog(catalogsAsOptions[0]);
    }

    if (!itemsSelectionState.products && catalogs.length > 0) {
        return <LoadingOverlay />;
    }

    const products =
        selectedCatalog && itemsSelectionState.products
            ? itemsSelectionState.products.map((product) => ({
                name: product.product_name,
                id: product.id,
                groupId: selectedCatalog.value,
            }))
            : [];

    return (
        <Fragment>
            <FieldArray
                name={name}
                render={(arrayHelpers) => (
                    <Fragment>
                        {items.length > 0 && (
                            <Table>
                                <Tbody>
                                    {items.map((item, itemIndex) => (
                                        <StyledTr tabIndex={itemIndex} key={item.id}>
                                            <Td>
                                                <StyledSpan isBlacklisted={item.isBlacklisted}>
                                                    {item.name
                                                        ? `${t('marketing.campaign.name')}: ${item.name}`
                                                        : `${t('marketing.campaign.id')}: ${item.id}`}
                                                </StyledSpan>
                                            </Td>
                                            <EdgeTd>
                                                <DeleteButton
                                                    type="button"
                                                    onClick={() => {
                                                        arrayHelpers.remove(itemIndex);
                                                    }}
                                                >
                                                    {'✕'}
                                                </DeleteButton>
                                            </EdgeTd>
                                        </StyledTr>
                                    ))}
                                </Tbody>
                            </Table>
                        )}
                        <AddItemWrapper>
                            <DropdownsContainer>
                                <MarketingDropdown
                                    name="catalog"
                                    value={
                                        selectedCatalog
                                            ? selectedCatalog
                                            : {
                                                label: t('marketing.campaign.no_catalogs'),
                                                value: 'no_catalogs',
                                            }
                                    }
                                    onChange={setSelectedCatalog}
                                    options={catalogsAsOptions}
                                />
                                <DropdownContainer>
                                    <DropdownButton type="button" onClick={() => setIsVisible(!isVisible)}>
                                        {selectedCatalog && products.length > 0
                                            ? t('marketing.campaign.select_items')
                                            : t('marketing.campaign.no_items')}
                                        <Icon icon="caret_down" />
                                    </DropdownButton>
                                    <ExpandableList visible={isVisible}>
                                        {selectedCatalog && products.length > 0 && (
                                            <ListItem>
                                                <Checkbox
                                                    name="selected_all_products"
                                                    label={t('marketing.campaign.select_all')}
                                                    checked={selectedProducts.length === products.length}
                                                    onChange={() => {
                                                        selectedProducts.length === products.length
                                                            ? setSelectedProducts([])
                                                            : setSelectedProducts(products);
                                                    }}
                                                />
                                            </ListItem>
                                        )}
                                        {selectedCatalog &&
                                            products.length > 0 &&
                                            products.map((product) => (
                                                <ListItem key={product.id}>
                                                    <Checkbox
                                                        key={product.id}
                                                        name="selected_products"
                                                        label={product.name}
                                                        value={product.id}
                                                        checked={selectedProducts
                                                            .map(({ id }) => id)
                                                            .includes(product.id)}
                                                        onChange={() => {
                                                            selectedProducts.map(({ id }) => id).includes(product.id)
                                                                ? setSelectedProducts(
                                                                    selectedProducts.filter(
                                                                        ({ id }) => id !== product.id
                                                                    )
                                                                )
                                                                : setSelectedProducts([
                                                                    ...selectedProducts,
                                                                    {
                                                                        name: product.name,
                                                                        id: product.id,
                                                                        groupId: selectedCatalog.value,
                                                                    },
                                                                ]);
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        {selectedCatalog && products.length > 0 && (
                                            <ButtonWrapper>
                                                <Button
                                                    type="button"
                                                    className="tiny"
                                                    onClick={() => setIsVisible(false)}
                                                >
                                                    {t('marketing.campaign.ok')}
                                                </Button>
                                            </ButtonWrapper>
                                        )}
                                    </ExpandableList>
                                </DropdownContainer>
                            </DropdownsContainer>
                            <AddButtonsWrapper>
                                <Button
                                    type="button"
                                    onClick={() => {
                                        selectedProducts.forEach((product) =>
                                            arrayHelpers.push({
                                                ...product,
                                                isBlacklisted: false,
                                            }));
                                        if (manuallyAddedIds.length > 0) {
                                            manuallyAddedIds.split(',').forEach((id) =>
                                                arrayHelpers.push({
                                                    id: id.trim(),
                                                    isBlacklisted: false,
                                                }));
                                        }
                                    }}
                                >
                                    {t('marketing.campaign.add_item')}
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => {
                                        selectedProducts.forEach((product) =>
                                            arrayHelpers.push({
                                                ...product,
                                                isBlacklisted: true,
                                            }));
                                        if (manuallyAddedIds.length > 0) {
                                            manuallyAddedIds.split(',').forEach((id) =>
                                                arrayHelpers.push({
                                                    id: id.trim(),
                                                    isBlacklisted: false,
                                                }));
                                        }
                                    }}
                                >
                                    {t('marketing.campaign.add_to_blacklist')}
                                </Button>
                            </AddButtonsWrapper>
                        </AddItemWrapper>
                        <ManualInputContainer>
                            <Input
                                name="manuallyAddedIds"
                                label={t('marketing.campaign.add_manually_items_ids')}
                                placeholder={t('marketing.campaign.items_ids_comma_separated')}
                                value={manuallyAddedIds}
                                onChange={(event) => setManuallyAddedIds(event.currentTarget.value)}
                                onBlur={handleBlur}
                            />
                        </ManualInputContainer>
                    </Fragment>
                )}
            />
        </Fragment>
    );
};

const DropdownsContainer = styled.div`
    display: flex;
    width: 65%;
    justify-content: space-between;

    > *:first-child {
        width: 40%;
    }

    @media (max-width: 768px) {
        display: block;
        width: 100%;

        > *:first-child {
            width: 100%;
        }
    }
`;

const DeleteButton = styled.button`
    padding-left: 0;
    color: ${colors.invalid};
    text-align: end;
    background: none;
    border: none;
    cursor: pointer;

    &:focus {
        outline: none;
    }
`;

const AddItemWrapper = styled.div`
    display: flex;
    margin-top: ${distances.small};
    justify-content: space-between;

    @media (max-width: 768px) {
        display: block;
    }
`;

const AddButtonsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    width: 30%;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const StyledTr = styled(Tr as any)`
    border-bottom: ${border.normal} solid ${colors.borderLight};
    cursor: pointer;

    &:focus {
        outline: none;
        border-bottom: ${border.normal} solid ${colors.primary};
    }
`;

const StyledSpan = styled.span<{ isBlacklisted: boolean }>`
    text-decoration: ${(props) => (props.isBlacklisted ? 'line-through' : 'none')};
`;

const EdgeTd = styled(Td as any)`
    text-align: end;
`;

const DropdownContainer = styled.div`
    position: relative;
    width: 55%;

    @media (max-width: 768px) {
        width: 100%;
        margin: ${distances.small} 0;
    }
`;

const DropdownButton = styled.button`
    width: 100%;
    padding: 6px;
    margin: 0;
    display: flex;
    justify-content: space-between;
    outline: none;
    align-items: center;
    background-color: ${colors.background};
    border: 2px solid ${colors.borderDark};
    cursor: pointer;
`;

const ExpandableList = styled.div<{ visible: boolean }>`
    position: absolute;
    width: 100%;
    display: ${(props) => (props.visible ? 'block' : 'none')};
    z-index: 90;
    outline: none;
    background-color: ${colors.background};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const ListItem = styled.div`
    padding: ${distances.small};
    cursor: pointer;
`;

const ButtonWrapper = styled.div`
    display: flex;
    padding: 0 ${distances.small} ${distances.small};
    justify-content: flex-end;
`;

const ManualInputContainer = styled.div`
    width: 65%;
    margin-top: ${distances.small};

    @media (max-width: 768px) {
        width: 100%;
    }
`;

export default ItemsSelection;
